import { Helmet } from "react-helmet-async";
import vidavoIndiaHero from "@/assets/vidavo-india-hero.jpg";
import mcmeLogo from "@/assets/mcme-techserve-logo.png";
import aiInnovationImg from "@/assets/ai-decision-support.png";
import { useMemo } from "react";
import { motion } from "framer-motion";
import { Globe, Handshake, Network, Activity, Video, HeartPulse, Users, BrainCircuit, MonitorPlay } from "lucide-react";
import Navigation from "@/components/landing/Navigation";
import Footer from "@/components/landing/Footer";
import { useTranslation } from "@/hooks/useTranslation";
import { hiIndia } from "@/i18n/hi-india";

const VidavoIndia = () => {
  const { t } = useTranslation();

  // Check if Hindi is requested via URL param
  const isHindi = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('lang') === 'hi';
  }, []);

  const d = isHindi ? hiIndia : t.vidavoIndia;
  const localPartnerLabel = isHindi ? hiIndia.localPartner : 'Local Strategic Partner';

  const solutionIcons = [Network, Activity, Video, HeartPulse, Users];

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-100 to-neutral-50 relative overflow-hidden">
      <Helmet>
        <title>{d.pageTitle}</title>
        <meta name="description" content={d.metaDescription} />
        <link rel="canonical" href="https://vidavo.eu/about/vidavo-asia" />
        <meta property="og:title" content={d.pageTitle} />
        <meta property="og:description" content={d.metaDescription} />
        <meta property="og:url" content="https://vidavo.eu/about/vidavo-asia" />
        <meta property="og:type" content="website" />
      </Helmet>
      <Navigation />

      {/* Hindi toggle button */}
      {!isHindi && (
        <div className="fixed top-32 left-1/2 -translate-x-1/2 w-[95%] max-w-6xl z-40 flex justify-end pr-6 pointer-events-none">
          <a
            href="/about/vidavo-asia?lang=hi"
            target="_blank"
            rel="noopener noreferrer"
            className="pointer-events-auto inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-accent-teal to-secondary text-white text-sm font-medium shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
            <Globe className="w-4 h-4" />
            Read this page in Hindi
          </a>
        </div>
      )}

      {/* Hero Section */}
      <header className="relative min-h-[50vh] md:min-h-[70vh] flex items-center justify-center pt-24 overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-[60%] bg-gradient-to-bl from-secondary/15 via-secondary/8 to-transparent blur-3xl" />
        <div className="absolute top-1/4 left-0 w-1/3 h-[50%] bg-gradient-to-r from-accent-teal/12 via-accent-teal/6 to-transparent blur-2xl" />
        <div className="absolute bottom-0 right-1/4 w-1/2 h-[40%] bg-gradient-to-t from-secondary/10 via-transparent to-transparent blur-3xl" />

        <div className="container-padding relative z-10 py-[5px] my-[20px] md:my-[50px]">
          <div className="max-w-6xl mx-auto relative">
            <div className="flex flex-col items-center justify-center gap-3 mb-6">
              <motion.div
                className="inline-flex items-center gap-2 bg-gradient-to-r from-secondary/10 to-accent-teal/10 
                             border border-secondary/20 rounded-full px-6 py-3 text-secondary font-medium
                             backdrop-blur-sm"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.1 }}>
                <Globe className="w-4 h-4" />
                {d.badge}
              </motion.div>
            </div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl mb-4 sm:mb-6 text-accent-teal font-bold leading-tight text-center">
              {d.heroTitle}
            </motion.h1>

            <motion.p
              className="text-lg sm:text-xl md:text-2xl text-secondary font-semibold max-w-3xl mx-auto mb-10 text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}>
              {d.heroText}
            </motion.p>
          </div>
        </div>
      </header>

      {/* Hero Image */}
      <div className="container-padding relative z-10 -mt-10 md:-mt-16">
        <div className="max-w-3xl mx-auto">
          <motion.img
            src={vidavoIndiaHero}
            alt={d.heroImageAlt}
            className="w-full rounded-2xl shadow-[var(--shadow-healthcare)] object-cover max-h-[350px]"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }} />
        </div>
      </div>

      <main className="py-16 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-[60%] bg-gradient-to-bl from-secondary/15 via-secondary/8 to-transparent blur-3xl" />
        <div className="absolute top-1/4 left-0 w-1/3 h-[50%] bg-gradient-to-r from-accent-teal/12 via-accent-teal/6 to-transparent blur-2xl" />

        <div className="container-padding relative z-10">
          <div className="max-w-4xl mx-auto space-y-16">

            {/* Who We Are Section */}
            <motion.section
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}>

              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 rounded-2xl bg-gradient-to-r from-accent-teal to-secondary text-white">
                  <Handshake className="w-8 h-8" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-primary">
                  {d.whoWeAreTitle}
                </h2>
              </div>
              <div className="space-y-4">
                {d.whoWeAreText?.split('\n\n').map((paragraph, idx) => (
                  <p key={idx} className="text-lg text-neutral-700 leading-relaxed">
                    {paragraph}
                  </p>
                ))}
              </div>

              {/* Partner logo */}
              <div className="flex flex-col items-center gap-2 mt-8">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-secondary/50">
                  {localPartnerLabel}
                </span>
                <img
                  src={mcmeLogo}
                  alt="Mc&Me Techserve logo"
                  className="h-14 sm:h-16 object-contain" />
              </div>
            </motion.section>

            {/* VIDA24 Platform Section */}
            <motion.section
              className="relative overflow-hidden rounded-2xl border border-secondary/15 bg-white/70 shadow-[var(--shadow-healthcare)]"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}>

              <div className="absolute inset-0 bg-gradient-to-br from-accent-teal/5 to-secondary/5" />
              <div className="relative z-10 px-6 py-10 sm:px-10 sm:py-12">
                <div className="mb-8 text-center sm:mb-10">
                  <span className="text-sm font-bold uppercase tracking-widest text-secondary">
                    {d.platformLabel}
                  </span>
                  <h2 className="mt-2 text-3xl font-bold text-accent-teal sm:text-4xl">
                    {d.platformTitle}
                  </h2>
                </div>

                <div className="grid items-center gap-8 sm:grid-cols-2 sm:gap-8 lg:gap-12">
                  <div className="flex flex-col items-center text-center">
                    <img
                      src="/lovable-uploads/vida24LOGO_4.png"
                      alt="VIDA24"
                      className="mb-6 h-20 w-auto object-contain sm:h-24"
                      loading="lazy" />

                    <div className="space-y-4">
                      {d.platformText?.split('\n\n').map((paragraph, idx) => (
                        <p key={idx} className="text-base leading-relaxed text-neutral-700 sm:text-lg">
                          {paragraph}
                        </p>
                      ))}
                    </div>

                    <a
                      href={`mailto:${d.ctaEmail}?subject=VIDA24%20Demo%20Request`}
                      className="button-primary group mt-7 inline-flex items-center gap-2 text-lg">
                      <MonitorPlay className="w-5 h-5" />
                      {d.demoButton}
                    </a>
                  </div>

                  <img
                    src="/lovable-uploads/66f7b12f-9c3f-451d-8916-47c1764be385.png"
                    alt="VIDA24 digital health platform on laptop and mobile devices"
                    className="mx-auto w-full max-w-lg object-contain"
                    loading="lazy" />
                </div>
              </div>
            </motion.section>

            {/* Our Solutions Section */}
            <motion.section
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}>

              <div className="text-center mb-8">
                <h2 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-primary via-secondary to-accent-teal bg-clip-text text-transparent mb-4 py-[10px]">
                  {d.solutionsTitle}
                </h2>
                <div className="w-20 h-1 bg-gradient-to-r from-accent-teal to-primary rounded-full mx-auto" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {d.solutions?.map((solution, index) => {
                  const SolutionIcon = solutionIcons[index] || Network;
                  return (
                    <motion.div
                      key={index}
                      className="flex flex-col items-start gap-3 p-5 rounded-xl border border-secondary/15 bg-white/60 backdrop-blur-sm shadow-sm hover:scale-[1.03] transition-transform duration-300"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}>
                      <div className="p-2.5 rounded-lg bg-gradient-to-br from-accent-teal to-secondary text-white">
                        <SolutionIcon className="w-5 h-5" />
                      </div>
                      <h3 className="font-semibold text-primary">{solution.title}</h3>
                      <p className="text-sm text-neutral-700 leading-relaxed">{solution.description}</p>
                    </motion.div>
                  );
                })}
              </div>
            </motion.section>

            {/* AI-Powered Digital Innovation Section */}
            <motion.section
              className="relative overflow-hidden rounded-2xl border border-accent-teal/20 bg-white/70 shadow-[var(--shadow-healthcare)]"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}>

              <div className="absolute inset-0 bg-gradient-to-br from-accent-teal/8 via-white/0 to-secondary/8" />
              <div className="relative z-10 px-6 py-9 sm:px-10 sm:py-11">
                <div className="grid items-center gap-8 sm:grid-cols-2 sm:gap-10">
                  <img
                    src={aiInnovationImg}
                    alt="VIDA24 connected care across home, clinic and veterinary settings"
                    className="mx-auto w-full max-w-md rounded-xl object-contain"
                    loading="lazy" />
                  <div>
                    <div className="flex items-center gap-4 mb-5">
                      <div className="p-3 rounded-2xl bg-gradient-to-r from-accent-teal to-secondary text-white shrink-0">
                        <BrainCircuit className="w-8 h-8" />
                      </div>
                      <h2 className="text-2xl sm:text-3xl font-bold text-primary">
                        {d.innovationTitle}
                      </h2>
                    </div>
                    <div className="space-y-4">
                      {d.innovationText?.split('\n\n').map((paragraph, idx) => (
                        <p key={idx} className="text-lg text-neutral-700 leading-relaxed">
                          {paragraph}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.section>

            {/* International Experience Section */}
            <motion.section
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}>

              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 rounded-2xl bg-gradient-to-r from-accent-teal to-secondary text-white">
                  <Globe className="w-8 h-8" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-primary">
                  {d.experienceTitle}
                </h2>
              </div>

              <div className="space-y-4">
                {d.experienceText?.split('\n\n').map((paragraph, idx) => (
                  <p key={idx} className="text-lg text-neutral-700 leading-relaxed">
                    {paragraph}
                  </p>
                ))}
              </div>
            </motion.section>
          </div>
        </div>
      </main>

      {/* Discover / CTA Section */}
      <section className="py-20">
        <div className="container-padding">
          <div className="max-w-3xl mx-auto text-center">
            <div className="flex justify-center mb-8">
              <div className="w-24 h-1 bg-gradient-to-r from-accent-teal to-secondary rounded-full" />
            </div>
            <motion.h2
              className="text-3xl md:text-4xl font-bold text-primary mb-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}>
              {d.discoverTitle}
            </motion.h2>
            <motion.p
              className="text-lg text-neutral-700 leading-relaxed mb-8"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}>
              {d.discoverText}
            </motion.p>
            <motion.div
              className="space-y-3"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}>
              <a
                href={`mailto:${d.ctaEmail}`}
                className="button-primary group text-lg inline-flex items-center gap-2">
                {d.contactButton}
              </a>
              <p className="text-base text-neutral-500 mt-2">{d.ctaEmail}</p>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default VidavoIndia;
